import React from "react"
import { useIntl } from "react-intl"
import { LocationDescriptor } from "history"
import { Redirect, Link as RouterLink, useLocation } from "react-router-dom"
import { Button, Stack, Typography } from "@carrotfertility/carotene-core"
import { PageHeader } from "#/features/global-ui/page-header"
import { PageLayout, PageLoading } from "#/features/global-ui/page-layout"
import { Title } from "#/services/page-title/Title"
import { useConsumerRoutesEnabled } from "#/services/launchdarkly/useConsumerRoutesEnabled"
import { useIsPaywalled } from "#/services/user/hooks/useSubscriptionStatus"
import { PaywallLocationState } from "#/components/views/app/routes/PaywallGate"

// Placeholder wording, held OUT of the translation pipeline on purpose: these are not the
// strings members will read, and extracting them ships three throwaway ids to Smartling to be
// translated into every locale and then churned again when the real copy lands. Plain constants
// rather than `<FormattedMessage>` for exactly that reason — the page is dark behind
// `consumerAppSignUp` until the copy ticket replaces them. (CONAPP-418)
const PLACEHOLDER_BODY = "Your subscription isn't active, so this part of Carrot is unavailable right now."
const PLACEHOLDER_CTA = "Go to billing detail"

// Where the member goes once they are no longer walled. The gate records the path it took away
// from them; anything else — a direct visit, an old history entry — goes home. History state is
// script-writable and outlives the redirect that set it, so neither the shape nor the contents of
// what comes back are trusted: `pathname` can be missing or not a string, and a second leading
// slash reads to the browser as a protocol-relative URL off this origin entirely — as does a
// leading backslash, which URL parsing normalises to one — which is not something a redirect
// built for in-app paths should ever be handed.
function destinationOnceAllowed(state: PaywallLocationState | undefined): LocationDescriptor {
  const from = state?.from
  if (typeof from?.pathname !== "string") return "/"
  if (!from.pathname.startsWith("/") || /^[/\\]{2}/.test(from.pathname)) return "/"
  return from
}

export default function PaywallPage(): JSX.Element {
  const intl = useIntl()
  const { state } = useLocation<PaywallLocationState | undefined>()
  const { isPaywalled, isLoading } = useIsPaywalled(useConsumerRoutesEnabled())
  // Deliberately not "Paywall": this is the level-1 heading and the browser tab title, and the
  // member reading it is the one being charged. Names the thing they need, not the mechanism.
  const pageTitle = intl.formatMessage({ defaultMessage: "Subscription" })

  // `isLoading` only — a background re-read of the status must not blink this page into a
  // spinner, and being here is already an allowed destination.
  if (isLoading) return <PageLoading />

  // The exact inverse of the route gate, so the two can never both hold and bounce a member
  // between `/` and here. `isPaywalled` is already false for an enterprise member, an active
  // consumer, and a status read that failed — the hook fails open — and each of those has no
  // business on this page.
  if (!isPaywalled) return <Redirect to={destinationOnceAllowed(state)} />

  return (
    <PageLayout variant="default" header={<PageHeader pageTitle={pageTitle} />}>
      {/* `titleMap` has no `/paywall` entry, so without this the tab reads "Page not found". */}
      <Title title={pageTitle} />
      <Stack sx={(theme) => ({ alignItems: "flex-start", gap: theme.spacing(theme.tokens.spacing.md) })}>
        <Typography>{PLACEHOLDER_BODY}</Typography>
        <Button component={RouterLink} to="/consumer/billing-detail">
          {PLACEHOLDER_CTA}
        </Button>
      </Stack>
    </PageLayout>
  )
}
